import { memo, useMemo, useReducer } from 'react';

import { useFormik } from 'formik';
import { SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import { ModalForm } from '../form';

import css from './ModalCard.module.css';

import { modalSettingsService } from 'v2/api/modalSettingsService';
import { Card } from 'v2/components/card/Card';
import { ButtonsFormControl } from 'v2/components/form/buttonsFormControl/ButtonsFormControl';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { ModalModel } from 'v2/types/ModalModel';
import { handleHttpError } from 'v2/utils';

interface ModalCardProps {
  modal: ModalModel;
  onRevalidate: VoidFunction;
}

export const ModalCard = memo<ModalCardProps>(({ modal, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { id: _id, ...modalFormValues } = modal;
  const initialValues = useMemo(() => modalFormValues, [modal]);

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateModal', modal.id],
    modalSettingsService.updateModal,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The modal has been successfully updated');
        setOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteModal', modal.id], modalSettingsService.deleteModal, {
    onSuccess: () => {
      onRevalidate();
    },
    onError: handleHttpError,
  });

  const formik = useFormik<ModalForm>({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      triggerUpdate(values);
    },
  });

  const handleChangeRichTextarea = (value: string) => {
    formik.setFieldValue('body', value);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen();
  };

  return (
    <Card className={css.modalCardContainer}>
      <p className={css.modalCardHeader}>{modal.header}</p>

      <div className={css.modalCardActions}>
        <IconButton onClick={setOpen} color="warning">
          <SquarePen width={20} height={20} />
        </IconButton>

        <Popconfirm message="Are you sure that you want to delete this modal?" onConfirm={triggerDelete}>
          <IconButton color="error">
            <Trash2 width={20} height={20} />
          </IconButton>
        </Popconfirm>
      </div>

      <Dialog open={isOpen} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
        <DialogTitle>Edit Modal</DialogTitle>

        <DialogContent>
          <form id="modalEditForm" className="form with-top-space" onSubmit={formik.handleSubmit}>
            <TextField
              name="header"
              value={formik.values.header}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Header"
              required
            />

            <RichTextarea value={formik.values.body} label="Description" onChange={handleChangeRichTextarea} />

            <TextField
              name="metricParam"
              value={formik.values.metricParam}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Metric Id"
              required
            />

            <ButtonsFormControl formik={formik} />
          </form>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>

          <Button form="modalEditForm" type="submit" variant="contained" loading={isMutating}>
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

ModalCard.displayName = 'ModalCard';
